import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {useTranslation} from '@/translations/i18n'
import {DiseaseExtended, TreatmentRequestDiagnosisValidationSchema} from '@/features/treatment-requests/types'
import {Grid, GridItem} from '@/components/ui/grid-atoms/GridAtoms'
import {InputSearchDisease} from '@/features/treatment-requests/components/input-search-disease/InputSearchDisease'
import {InputSelectPathology} from '@/features/projects/components/input-select-pathology/InputSelectPathology'
import {Controller, useFieldArray, useFormContext, useWatch} from 'react-hook-form'
import {DiseasesGroupsRadioButtons} from '@/features/treatment-requests/components/diseases-groups-radio-buttons/DiseasesGroupsRadioButtons'
import {DiseasesGroupsSelectors} from '@/features/treatment-requests/components/diseases-groups-selectors/DiseasesGroupsSelectors'
import {InputDate} from '@/components/commons/input-date-legacy/InputDate'
import dayjs from 'dayjs'
import {PatientAgeRange} from '@/types'
import {Text} from '@/components/ui/text/Text'
import {useCallback, useEffect} from 'react'
import {ObjectValues} from '@/utilities/helpers'
import {DiagnosisCodesLinkToggle} from '@/features/projects/components/diagnosis-codes-link-toggle/DiagnosisCodesLinkToggle'
import {DiagnosisCodesPair, useDiagnosisCodesLink} from '@/features/projects/hooks/useDiagnosisCodesLink'

export const TreatmentRequestDiagnosisForm = ({
    serviceType
}: {
    serviceType: Exclude<PatientAgeRange, 'evolutionary_age_adults'>
}) => {
    const {t} = useTranslation()
    const form = useFormContext<TreatmentRequestDiagnosisValidationSchema>()
    const diseases = useWatch({control: form.control, name: 'diseases'})
    const scores = useWatch({control: form.control, name: 'scores'})
    const diagnosisCode = useWatch({control: form.control, name: 'diagnosisCode'})
    const icd10DiagnosisCode = useWatch({control: form.control, name: 'icd10DiagnosisCode'})

    const handleChangeDiagnosisCodes = useCallback(
        ({icd9, icd10}: DiagnosisCodesPair) => {
            form.setValue('diagnosisCode', icd9, {shouldDirty: true})
            form.setValue('icd10DiagnosisCode', icd10, {shouldDirty: true})
        },
        [form]
    )

    const {isLinked, isIcd9Selectable, onToggleLink, handleSelectIcd9, handleSelectIcd10} = useDiagnosisCodesLink({
        icd9: diagnosisCode,
        icd10: icd10DiagnosisCode,
        onChange: handleChangeDiagnosisCodes
    })

    const {fields, remove, replace} = useFieldArray({
        name: 'diseases',
        control: form.control,
        keyName: 'key'
    })

    const handleSelectDiseaseGroups = (value: DiseaseExtended) => {
        const parents = []
        let currentParent = value.parent

        while (currentParent) {
            parents.unshift(currentParent)
            currentParent = currentParent.parent
        }

        replace([...parents, value])
    }

    useEffect(() => {
        if (
            diseases &&
            diseases.some(disease => disease.name != 'I° Gruppo') &&
            scores &&
            ObjectValues(scores).some(type => typeof type == 'string' && type == 'subtype')
        ) {
            form.setValue(
                'scores',
                scores.filter(score => score.type != 'subtype')
            )
        }
    }, [diseases, scores])

    return (
        <Flexbox direction="column" gap={6} fullWidth>
            <Grid columns={6} fullWidth>
                <GridItem spanColumn={4}>
                    <Controller
                        control={form.control}
                        name="diseases"
                        render={() => (
                            <InputSearchDisease
                                serviceType={serviceType}
                                placeholder={t('treatment_requests:search_diseases_groups')}
                                onChange={value => {
                                    if (!!value.parent?.id && value.parent.id != value.id) {
                                        handleSelectDiseaseGroups(value)
                                    } else {
                                        replace([value])
                                    }
                                }}
                                onClear={() => replace([])}
                            />
                        )}
                    />
                </GridItem>
            </Grid>

            <Flexbox direction="column" gap={4} fullWidth>
                <Text size="textSm" palette="neutral" shade="500">
                    {t('treatment_requests:select_disease_group_label')}
                </Text>
                <Flexbox direction="column" gap={5} fullWidth>
                    <DiseasesGroupsRadioButtons serviceType={serviceType} onChange={replace} />

                    {fields?.length > 0 && (
                        <DiseasesGroupsSelectors
                            fields={fields}
                            serviceType={serviceType}
                            onChange={handleSelectDiseaseGroups}
                            onClear={index =>
                                fields.map((_, fieldIndex) => (fieldIndex > index ? remove(fieldIndex) : null))
                            }
                        />
                    )}

                    {fields.length > 0 && (
                        <Controller
                            control={form.control}
                            name="diseaseOnsetDate"
                            render={({field}) => (
                                <InputDate
                                    label={t('treatment_requests:disease_onset_date')}
                                    isOptional
                                    placeholder="GG/MM/AAAA"
                                    errorMessage={form.formState.errors.diseaseOnsetDate?.message}
                                    maxDate={dayjs().toDate()}
                                    numMonths={1}
                                    selectedDates={field.value ? [field.value] : []}
                                    onChange={field.onChange}
                                />
                            )}
                        />
                    )}
                </Flexbox>
            </Flexbox>

            {/* Laid out as a row rather than as grid cells so the chain toggle can sit between the
                two selects, on their input line. */}
            <Flexbox fullWidth gap={2.5} align="flex-end">
                <div style={{flex: '1 1 0', minWidth: 0}}>
                    <InputSelectPathology
                        label={t('treatment_requests:icd9_code')}
                        placeholder={t('treatment_requests:icd9_code_placeholder')}
                        value={diagnosisCode}
                        onChange={handleSelectIcd9}
                        disabled={!isIcd9Selectable}
                        isClearable
                        version="ICD9"
                    />
                </div>
                <DiagnosisCodesLinkToggle isLinked={isLinked} isEditable={isIcd9Selectable} onToggle={onToggleLink} />
                <div style={{flex: '1 1 0', minWidth: 0}}>
                    <InputSelectPathology
                        label={t('treatment_requests:icd10_code')}
                        placeholder={t('treatment_requests:icd10_code_placeholder')}
                        value={icd10DiagnosisCode}
                        onChange={handleSelectIcd10}
                        isClearable
                        version="ICD10"
                    />
                </div>
            </Flexbox>
        </Flexbox>
    )
}
